import { api } from './api';

/**
 * ZGW connector (case system): settings (company owner or global ADMIN),
 * status, import from a case and write-back. Hand-written until the next
 * `yarn syncApis`. Errors are silent: the components show their own
 * localized messages from the neutral error codes.
 */
export type ZgwConnectionRo = {
  enabled: boolean;
  zakenApiUrl: string;
  documentenApiUrl: string;
  clientId: string;
  /** The secret itself is write-only and never returned. */
  hasSecret: boolean;
  secretUpdatedAt?: string;
  informatieobjecttypeUrl: string;
  auteur: string;
  updatedAt?: string;
};

export type ZgwSettingsRo = {
  companyId: string;
  connection: ZgwConnectionRo | null;
};

export type ZgwConnectionUpsertDto = {
  enabled: boolean;
  zakenApiUrl: string;
  documentenApiUrl: string;
  clientId: string;
  /** Empty or left out: the stored secret stays. */
  clientSecret?: string;
  informatieobjecttypeUrl: string;
  auteur: string;
};

export type ZgwTestResultRo = { ok: boolean; code?: string; step?: string };

export type ZgwNotImportableReason = 'format' | 'tooLarge' | 'otherHost' | 'unavailable';

export type ZgwDocumentRo = {
  informatieobjectUrl: string;
  titel?: string;
  bestandsnaam?: string;
  formaat?: string;
  bestandsomvang?: number | null;
  vertrouwelijkheidaanduiding?: string;
  importable: boolean;
  reason?: ZgwNotImportableReason;
};

export type ZgwZaakDocumentsRo = { identificatie: string; documents: ZgwDocumentRo[] };

export type ZgwWriteBackState = 'creating' | 'created' | 'linked';

export type ZgwTaskRo = {
  imported: boolean;
  titel?: string;
  vertrouwelijkheidaanduiding?: string;
  writeBackTitel?: string;
  writeBackState?: ZgwWriteBackState;
  writtenBackAt?: string;
};

export type ZgwWriteBackRo = { state: 'created' | 'linked'; writtenBackAt?: string };

/** Neutral error code from an API error ({ data: { code } }). */
export const zgwErrorCode = (error: unknown): string | undefined => (error as { data?: { code?: string } } | undefined)?.data?.code;

const ZGW_SETTINGS_TAG = 'ZgwSettings' as const;
const ZGW_STATUS_TAG = 'ZgwStatus' as const;
const ZGW_TASK_TAG = 'ZgwTask' as const;

const withCompany = (companyId?: string) => (companyId ? { companyId } : undefined);

export const zgwApi = api.enhanceEndpoints({ addTagTypes: [ZGW_SETTINGS_TAG, ZGW_STATUS_TAG, ZGW_TASK_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    zgwSettings: build.query<ZgwSettingsRo, { companyId?: string } | void>({
      query: (arg) => ({
        url: '/api/v1/company/zgw',
        params: withCompany(arg ? arg.companyId : undefined),
        silentErrors: true,
      }),
      providesTags: [ZGW_SETTINGS_TAG],
    }),
    zgwSettingsSave: build.mutation<ZgwSettingsRo, { body: ZgwConnectionUpsertDto; companyId?: string }>({
      query: ({ body, companyId }) => ({
        url: '/api/v1/company/zgw',
        method: 'PUT',
        body,
        params: withCompany(companyId),
        silentErrors: true,
      }),
      invalidatesTags: [ZGW_SETTINGS_TAG, ZGW_STATUS_TAG],
    }),
    zgwSettingsDelete: build.mutation<void, { companyId?: string }>({
      query: ({ companyId }) => ({
        url: '/api/v1/company/zgw',
        method: 'DELETE',
        params: withCompany(companyId),
        silentErrors: true,
      }),
      invalidatesTags: [ZGW_SETTINGS_TAG, ZGW_STATUS_TAG],
    }),
    zgwTest: build.mutation<ZgwTestResultRo, { companyId?: string }>({
      query: ({ companyId }) => ({
        url: '/api/v1/company/zgw/test',
        method: 'POST',
        params: withCompany(companyId),
        silentErrors: true,
      }),
    }),
    zgwStatus: build.query<{ enabled: boolean }, void>({
      query: () => ({ url: '/api/v1/zgw/status', silentErrors: true }),
      providesTags: [ZGW_STATUS_TAG],
    }),
    // POST: the case identifier stays out of URLs and request logs.
    zgwZaakDocuments: build.mutation<ZgwZaakDocumentsRo, { identificatie: string }>({
      query: (body) => ({
        url: '/api/v1/zgw/zaak-documents',
        method: 'POST',
        body,
        silentErrors: true,
      }),
    }),
    zgwImport: build.mutation<{ id: string }, { identificatie: string; informatieobjectUrl: string; workspaceId: string; folderId?: string }>({
      query: (body) => ({
        url: '/api/v1/zgw/import',
        method: 'POST',
        body,
        silentErrors: true,
      }),
    }),
    zgwTask: build.query<ZgwTaskRo, { taskId: string }>({
      query: ({ taskId }) => ({ url: `/api/v1/zgw/tasks/${taskId}`, silentErrors: true }),
      providesTags: (_r, _e, { taskId }) => [{ type: ZGW_TASK_TAG, id: taskId }],
    }),
    zgwWriteBack: build.mutation<ZgwWriteBackRo, { taskId: string }>({
      query: ({ taskId }) => ({
        url: `/api/v1/zgw/tasks/${taskId}/write-back`,
        method: 'POST',
        silentErrors: true,
      }),
      invalidatesTags: (_r, _e, { taskId }) => [{ type: ZGW_TASK_TAG, id: taskId }],
    }),
  }),
});

export const {
  useZgwSettingsQuery,
  useZgwSettingsSaveMutation,
  useZgwSettingsDeleteMutation,
  useZgwTestMutation,
  useZgwStatusQuery,
  useZgwZaakDocumentsMutation,
  useZgwImportMutation,
  useZgwTaskQuery,
  useZgwWriteBackMutation,
} = zgwApi;
